import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import {
    type DefaultValues,
    type FieldValues,
    type Resolver,
    useForm,
    type UseFormReturn
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { type z } from 'zod';

import { differences, removeEmptyValues } from '@/helpers/utils';

export interface UseEntityFormOptions<TValues extends FieldValues> {

    /** Zod schema for the form. The hook calls `zodResolver(schema)`. */
    schema: z.ZodTypeAny;

    /**
     * Computed default values for the form. Caller is responsible for
     * merging entity data + any form-local fields (e.g. `cupNotSpecific`)
     * before passing them in. The same object is used as the diff baseline
     * for PATCH payloads in edit mode.
     */
    initialValues: TValues;

    /** `true` when no entity exists yet (POST flow); `false` when editing (PATCH flow). */
    isCreateMode: boolean;

    /**
     * Called with the computed payload (POST: `removeEmptyValues(values)`;
     * PATCH: `differences(initialValues, values)`). Should throw on API
     * failure so the hook can surface the error in `saveError`.
     */
    onSave: (payload: Partial<TValues>) => Promise<unknown>;

    /**
     * Strip form-only fields before diff/submit. Optional — defaults to identity.
     * E.g. ProductForm uses this to drop the `cupNotSpecific` UI flag.
     */
    toApiPayload?: (values: TValues) => Partial<TValues>;

    /**
     * Fields whose empty value (`[]`) is meaningful on create and must
     * survive `removeEmptyValues`, so the server default doesn't replace an
     * explicit "none/all" choice. E.g. CompanyDialog's `discount_product_types`.
     */
    preserveEmptyOnCreate?: ReadonlyArray<keyof TValues & string>;

    /**
     * Whether the form's host (dialog/page) is currently visible. Used to
     * reset + re-validate the form whenever the host opens against a new
     * `resetKey`. For permanently-mounted forms, pass `true`.
     */
    open: boolean;

    /**
     * Opaque key whose change indicates the form should re-bind to a new
     * `initialValues`. Typically the entity's identifier (`entity?.uuid`).
     * `null`/`undefined` is fine for create-mode dialogs.
     */
    resetKey?: string | number | null;

    /** Called after a successful save AND after a no-op close. */
    onClose: () => void;
}

export interface UseEntityFormReturn<TValues extends FieldValues> {

    /** The RHF form instance. Spread into a `<FormProvider>`. */
    form: UseFormReturn<TValues>;

    /**
     * Wire into the `<form>` element:
     *   `<form onSubmit={form.handleSubmit(onSubmit)}>`
     *
     * Handles the toApiPayload → differences/removeEmpty → no-op → try/catch
     * pipeline. Calls `onClose()` on success or no-op.
     */
    onSubmit: (values: TValues) => Promise<void>;

    /** Server-side error message to display above the form, if any. */
    saveError: string | null;

    /** Clear `saveError` — call when the dialog re-opens / form is reset. */
    clearSaveError: () => void;
}

/**
 * Re-adds the listed fields that `removeEmptyValues` dropped because they
 * were empty, so an intentional empty value still reaches the API.
 */
export const preserveEmpty = <TValues extends FieldValues>(
    payload: Partial<TValues>,
    submitted: Partial<TValues>,
    keys: ReadonlyArray<keyof TValues & string>
): Partial<TValues> => {
    return keys.reduce<Partial<TValues>>((acc, key) => {
        return key in submitted && !(key in acc) ? { ...acc, [key]: submitted[key] } : acc;
    }, payload);
};

/**
 * Shared entity-edit form pipeline. Encapsulates the boilerplate that
 * repeats across every entity-dialog: `useForm()` with `zodResolver`, the
 * open/reset/trigger effect, the toApiPayload → diff → no-op-short-circuit
 * → try/catch submit pipeline, and `saveError` state.
 *
 * @example
 *   const { form, onSubmit, saveError } = useEntityForm({
 *       schema: productSchema,
 *       initialValues,
 *       isCreateMode: !entity?.uuid,
 *       onSave,
 *       toApiPayload,
 *       open,
 *       resetKey: entity?.uuid,
 *       onClose: () => setOpen(false),
 *   });
 *
 *   return (
 *       <FormProvider { ...form }>
 *           <form onSubmit={form.handleSubmit(onSubmit)}>
 *               { saveError && <ErrorBanner message={saveError} /> }
 *               <ProductFields entity={entity} />
 *               <StandardButtons onCancel={...} />
 *           </form>
 *       </FormProvider>
 *   );
 */
export function useEntityForm<TValues extends FieldValues>({
    schema,
    initialValues,
    isCreateMode,
    onSave,
    toApiPayload,
    preserveEmptyOnCreate = [],
    open,
    resetKey,
    onClose,
}: UseEntityFormOptions<TValues>): UseEntityFormReturn<TValues> {
    const [
        t,
    ] = useTranslation('pages');
    const [
        saveError,
        setSaveError,
    ] = useState<string | null>(null);

    const form = useForm<TValues>({
        // The schema is typed `z.ZodTypeAny` (caller-supplied), so zodResolver
        // can't infer back to TValues — cast through Resolver<TValues>.
        resolver: zodResolver(schema as never) as Resolver<TValues>,
        defaultValues: initialValues as DefaultValues<TValues>,
        mode: 'onChange',
    });

    const toApi = toApiPayload ?? ((v: TValues) => {
        return v as Partial<TValues>;
    });

    // Reset + validate on open / when the bound entity changes. RHF retains
    // its form instance across opens, so this is necessary — without it, an
    // already-valid entity would render with the validation indicator stale.
    // Only trigger validation in edit mode; in create mode the form starts
    // empty so triggering would surface "required" errors before the user
    // has touched anything.
    useEffect(() => {
        if (open) {
            form.reset(initialValues as DefaultValues<TValues>);
            if (!isCreateMode) {
                form.trigger();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        open,
        resetKey,
    ]);

    // Clear any prior server error during render, snapshotted on the same
    // `open`/`resetKey` axis as the form reset above. Keeping setSaveError out
    // of the effect avoids the react-hooks/set-state-in-effect violation.
    const [
        resetCycleKey,
        setResetCycleKey,
    ] = useState<string>(`${ open }-${ resetKey ?? '' }`);
    const currentCycleKey = `${ open }-${ resetKey ?? '' }`;

    if (open && resetCycleKey !== currentCycleKey) {
        setResetCycleKey(currentCycleKey);
        setSaveError(null);
    }

    const onSubmit = async (values: TValues): Promise<void> => {
        setSaveError(null);
        const submitted = toApi(values);
        const payload = isCreateMode
            ? preserveEmpty(removeEmptyValues(submitted) as Partial<TValues>, submitted, preserveEmptyOnCreate)
            : differences(toApi(initialValues), submitted) as Partial<TValues>;

        // No-op on an existing entity: skip the PATCH call entirely.
        if (!isCreateMode && Object.keys(payload).length === 0) {
            toast.info(t('No changes to save'));
            onClose();
            return;
        }

        try {
            await onSave(payload);
            onClose();
        } catch (error) {
            const message = (error as { response?: { data?: { message?: string } } })?.response?.data?.message
                ?? t('Something went wrong. Please try again.');

            setSaveError(message);
            throw error;
        }
    };

    return {
        // useForm's internal TFieldValues parameter is wider than TValues; cast
        // back to the public UseFormReturn<TValues> contract callers expect.
        form: form as unknown as UseFormReturn<TValues>,
        onSubmit,
        saveError,
        clearSaveError: () => {
            return setSaveError(null);
        },
    };
}
